import { ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import {
  formatDateTimeWithMilitarySupport,
  LuxonDateTimeFormats,
  MessageSourceType
} from '@nova/core';
import messageThreadService from '@satellite/services/message-thread-service';

export default function useMessageThread(locale = { value: 'en' }) {
  const messageThreadRef = ref({});
  const store = useStore();
  const { t } = useI18n();
  const fetchMessageThread = async assetVisitId => {
    if (!assetVisitId) {
      return;
    }
    const response = await messageThreadService.getMessageThreadByAssetVisitId(assetVisitId);

    if (response) {
      messageThreadRef.value = response;
    }
  };

  const sendMessage = async (messageThread, formData, requestConfig = {}) => {
    return messageThreadService.sendMessage(messageThread.id, formData, {}, requestConfig);
  };

  const postLastReadMessage = async (messageThread, messageId) => {
    if (!messageThread) {
      messageThread = messageThreadRef.value;
    }

    if (messageThread?.assetVisitId) {
      await fetchMessageThread(messageThread.assetVisitId);
      messageThread = messageThreadRef.value;
    }

    if (messageThread.lastReadMessageId === messageId) {
      return;
    }
    await messageThreadService.updateLastReadMessage(messageThread.id, messageId);
  };

  const isIncomingMessage = message => {
    return message.sourceType === MessageSourceType.DRIVER;
  };

  const isOutgoingMessage = message => {
    return !isIncomingMessage(message);
  };

  const isSystemMessage = message => {
    return message.sourceType === MessageSourceType.SYSTEM;
  };

  const getIncomingMessages = messages => {
    return messages.filter(isIncomingMessage);
  };

  const getLastUnreadIncomingMessage = (messages, lastReadId) => {
    const incMessages = getIncomingMessages(messages);
    const lastReadIndex = incMessages.findIndex(message => message.id === lastReadId);
    return incMessages[lastReadIndex];
  };

  // Is the message the last unread message in the thread?
  const isLastReadMessage = (message, messages, lastReadId) => {
    if (!message || !messages?.length || !lastReadId) {
      return false;
    }
    const lastMessage = getLastUnreadIncomingMessage(messages, lastReadId);
    return lastMessage?.id === message.id;
  };

  // Is the last message of the message thread read?
  const isLastMessageRead = messageThread => {
    const lastMessage = messageThread.messages[messageThread.messages.length - 1];
    if (isSystemMessage(lastMessage)) {
      return true;
    }
    return lastMessage.id === messageThread.lastReadMessageId;
  };

  const makeChatTimestamp = (date, warehouse, includeDate = true, includeTime = true) => {
    const dateTime = DateTime.fromISO(date).setZone(warehouse.timezone);
    const now = DateTime.now().setZone(warehouse.timezone);

    if (!includeDate && includeTime) {
      return formatDateTimeWithMilitarySupport(
        dateTime,
        warehouse.timezone,
        LuxonDateTimeFormats.Extended12HrTimeAMPM,
        store.getters['Settings/isMilitaryTimeEnabled'](warehouse),
        LuxonDateTimeFormats.Extended24HrTime,
        locale.value
      );
    }

    const timeFormat = includeTime
      ? ` • ${formatDateTimeWithMilitarySupport(
          dateTime,
          warehouse.timezone,
          LuxonDateTimeFormats.Extended12HrTimeAMPM,
          store.getters['Settings/isMilitaryTimeEnabled'](warehouse),
          LuxonDateTimeFormats.Extended24HrTime,
          locale.value
        )}`
      : '';

    if (includeDate) {
      if (dateTime.hasSame(now, 'day')) {
        return `${t('assetVisit.components.driverChatWindow.messages.today')}${timeFormat}`;
      } else if (dateTime.hasSame(now.minus({ days: 1 }), 'day')) {
        return `${t('assetVisit.components.driverChatWindow.messages.yesterday')}${timeFormat}`;
      }
      return dateTime.toFormat(`MMMM d yyyy${timeFormat}`);
    }

    return '';
  };

  const toggleThreadOpenStatus = async messageThread => {
    if (messageThread.isOpen) {
      return messageThreadService.closeMessageThread(messageThread.id);
    }
    return messageThreadService.openMessageThread(messageThread.id);
  };

  const toggleThreadTranslationEnabled = async messageThread => {
    if (messageThread.translationEnabled) {
      return messageThreadService.disableMessageThreadTranslation(messageThread.id);
    }
    return messageThreadService.enableMessageThreadTranslation(messageThread.id);
  };

  const reopenThread = async messageThread => {
    return messageThreadService.reopenMessageThread(messageThread.id);
  };

  return {
    messageThreadRef,
    fetchMessageThread,
    isIncomingMessage,
    isSystemMessage,
    postLastReadMessage,
    isLastReadMessage,
    getLastUnreadIncomingMessage,
    getIncomingMessages,
    isLastMessageRead,
    sendMessage,
    makeChatTimestamp,
    toggleThreadOpenStatus,
    toggleThreadTranslationEnabled,
    reopenThread,
    isOutgoingMessage
  };
}
